import { useMemo, useState } from 'react';
import { Plus, UserRound } from 'lucide-react';
import type { User } from '@cortex/domain';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Skeleton } from '@/components/ui/shadcn/skeleton';
import { StatusBadge, toneForLifecycleStatus } from '@/components/ui/StatusBadge';
import { EmptyState } from '@/components/ui/EmptyState';

/**
 * Master pane for the Users tab.
 *
 * Pure presentation — data and selection come from props so the parent
 * (`UsersTab`) owns network fetches. Built on the same shadcn primitives
 * and shared `StatusBadge` / `EmptyState` as the Entities tab so the two
 * admin surfaces feel identical.
 *
 * UX choices mirror EntityList: sticky filter bar, client-side search
 * over name + email, and an empty state that distinguishes first-run
 * from filtered-to-nothing.
 */

const FILTER_SELECT =
  'h-8 rounded-md border border-input bg-background px-2 text-xs shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none';

export interface UserListProps {
  readonly users: readonly User[];
  readonly selectedUserId: string | null;
  readonly isLoading: boolean;
  readonly errorMessage?: string | null;
  /** When true the company column is shown (global admins see many entities). */
  readonly showEntityColumn: boolean;
  readonly onSelect: (userId: string) => void;
  readonly onCreateNew: () => void;
}

type StatusFilter = 'all' | 'Active' | 'Suspended';

export function UserList({
  users,
  selectedUserId,
  isLoading,
  errorMessage,
  showEntityColumn,
  onSelect,
  onCreateNew,
}: UserListProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const knownEntities = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) map.set(u.entityId, u.entityName);
    return [...map.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [users]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (entityFilter !== 'all' && u.entityId !== entityFilter) return false;
      if (needle.length === 0) return true;
      if (u.displayName.toLowerCase().includes(needle)) return true;
      if (u.email.toLowerCase().includes(needle)) return true;
      return false;
    });
  }, [users, statusFilter, entityFilter, search]);

  const hasFilters = statusFilter !== 'all' || entityFilter !== 'all' || search !== '';

  return (
    <>
      <div className="space-y-2 border-b border-border bg-muted/40 px-3 py-3">
        <div className="flex gap-2">
          {showEntityColumn && (
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className={cn(FILTER_SELECT, 'flex-1')}
              aria-label="Filter by company"
            >
              <option value="all">All companies</option>
              {knownEntities.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          )}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className={cn(FILTER_SELECT, showEntityColumn ? '' : 'flex-1')}
            aria-label="Filter by status"
          >
            <option value="all">All status</option>
            <option value="Active">Active</option>
            <option value="Suspended">Suspended</option>
          </select>
        </div>
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="h-8 text-xs"
          aria-label="Search users"
        />
        <Button type="button" onClick={onCreateNew} size="sm" className="w-full">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Invite user
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && <ListSkeleton />}
        {!isLoading && errorMessage && (
          <div
            className="border-b border-destructive/20 bg-destructive/10 px-4 py-6 text-center text-xs text-destructive"
            role="alert"
          >
            {errorMessage}
          </div>
        )}
        {!isLoading && !errorMessage && filtered.length === 0 && (
          <EmptyState
            icon={UserRound}
            title={hasFilters ? 'No users match these filters.' : 'No users yet.'}
            description={
              hasFilters
                ? 'Try clearing the search or selecting “All status”.'
                : 'Use “Invite user” above to add your first person.'
            }
          />
        )}
        {!isLoading && !errorMessage && filtered.length > 0 && (
          <ul className="divide-y divide-border">
            {filtered.map((user) => {
              const selected = selectedUserId === user.id;
              return (
                <li key={user.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(user.id)}
                    className={cn(
                      'w-full border-l-4 px-4 py-3 text-left transition-colors focus:bg-accent focus:outline-none',
                      selected
                        ? 'border-l-primary bg-primary/5'
                        : 'border-l-transparent hover:bg-accent',
                    )}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-bold text-foreground">
                          {user.displayName}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                        {showEntityColumn && (
                          <div className="mt-0.5 truncate text-xs text-muted-foreground">
                            {user.entityName}
                          </div>
                        )}
                      </div>
                      <StatusBadge tone={toneForLifecycleStatus(user.status)} label={user.status} />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

function ListSkeleton() {
  return (
    <ul className="divide-y divide-border" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="space-y-2 px-4 py-3">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-2 w-1/2" />
        </li>
      ))}
    </ul>
  );
}
